import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { AxiosRequest } from "../ApiCall";
import { configEnv } from "../constant";

/** Phase 1 audit form builder - matches backend AuditForm payload */
export type AuditFormFlagRulePayload = {
    name: string;
    type:
        | "flat_underperformance"
        | "sudden_drop"
        | "combined_check"
        | "overall_floor"
        | "evidence_minimum";
    severity: "critical" | "major" | "minor";
    parameterId?: string;
    parameterIndex?: number;
    threshold?: number;
    cycles?: number;
    dropAmount?: number;
    parameterAId?: string;
    parameterBId?: string;
    parameterAIndex?: number;
    parameterBIndex?: number;
    thresholdA?: number;
    thresholdB?: number;
    overallThreshold?: number;
    evidenceMinPercent?: number;
};

export type AuditFormCorrelationPayload = {
    type: "causal_same_cycle" | "lagged_next_cycle";
    description?: string;
    parameterAId?: string;
    parameterBId?: string;
    parameterAIndex?: number;
    parameterBIndex?: number;
};

export type AuditFormContextQuestionPayload = {
    text: string;
    routing:
        | "auditor_pre"
        | "auditor_post"
        | "auditee_pre"
        | "auditee_post"
        | "cross_check";
    answerType:
        | "short_text"
        | "long_text"
        | "number"
        | "time_range"
        | "date"
        | "single_choice"
        | "multiple_choice"
        | "scale_1_5"
        | "yes_no_text";
    choices?: string[];
    auditorPhrasing?: string;
    auditeePhrasing?: string;
    order: number;
    /** When routing is auditee_post: marks post-audit vs legacy pre-observation (omit = legacy pre) */
    contextTiming?: "pre_audit" | "post_audit";
};

export type AuditFormCreateBody = {
    title: string;
    version: number;
    auditCategory: string;
    organizationId?: string;
    isPACT: boolean;
    createdBy?: string;
    feedbackQuestions: string[];
    parameters: Array<{
        name: string;
        criteria: Array<{
            name: string;
            description?: string;
            expectations: Record<string, string>;
        }>;
        triggerScore?: unknown[];
    }>;
    flagRules?: AuditFormFlagRulePayload[];
    correlations?: AuditFormCorrelationPayload[];
    /** Plain-text DSL; backend parses into flagRules */
    flagRulesText?: string;
    /** Plain-text DSL; backend parses into correlations */
    correlationsText?: string;
    contextQuestions?: AuditFormContextQuestionPayload[];
    aiDirective?: { content: string; tone: string };
};

export const auditApi = {
    create: async (body: AuditFormCreateBody, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/create-audit-form`,
                body,
                header
            );
            minimalToast({
                title: "Audit Created",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    createAuditCategory: async (
        body: { name: string; description?: string; organizationId: string },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/audit-category`,
                body,
                header
            );
            minimalToast({
                title: "Audit Category Created",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    calculateScores: async (
        body: { auditId: string; scores: Record<string, number> },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/calculate-scores`,
                body,
                header
            );
            minimalToast({
                title: "Scores Calculated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    assignAuditCategoryToOrganization: async (
        body: { organizationId: string; categoryId: string },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/assign-audit-category-to-organization`,
                body,
                header
            );
            minimalToast({
                title: "Audit Category Assigned To Organization",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    scheduleAudit: async (
        body: { auditId: string; scheduledDate: Date; auditorId: string },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/schedule-audit`,
                body,
                header
            );
            minimalToast({
                title: "Audit Scheduled",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    conductAudit: async (
        body: { auditId: string; findings: string; status: string },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/conduct-audit`,
                body,
                header
            );
            minimalToast({
                title: "Audit Conducted",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    togglePublishAudit: async (
        id: string,
        body: { isPublished: boolean },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/audit/${id}/toggle-publish`,
                body,
                header
            );
            minimalToast({
                title: "Publish Status Toggled",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    getAllAuditForm: async (
        query: { auditCategoryId?: string } = {},
        header: any
    ) => {
        try {
            const queryParams = new URLSearchParams(query).toString();
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/get-all-audit-form?${queryParams}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    getAllAuditCategoryByFilters: async (
        filters: { name?: string; status?: string },
        header: any
    ) => {
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-categories/filter?${queryParams}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    /**
     * Deliberately does not swallow its own errors: callers that need to
     * treat "unknown" differently from "resolved false" (e.g. the Students
     * tab's financial-literacy check, which must not assume a category is
     * growth just because this lookup failed) need the rejection to reach
     * them. `silentError` skips the global toast interceptor - this is
     * increasingly used as a plain data lookup, not a user-initiated action,
     * so a raw "Failed to fetch audit category" toast would surface out of
     * context. A caller that wants its own error handling passes `header`;
     * one that wants a user-facing message shows it deliberately, in place.
     */
    getAuditCategoryById: async (id: string, header: any) => {
        const data = await AxiosRequest(
            "GET",
            `${configEnv.baseUrl}/audit/audit-category/${id}`,
            null,
            header,
            { silentError: true }
        );
        return data.data;
    },
    getAuditCategoryByOrganizationId: async (
        id: string,
        query: { noPopulate?: boolean } = { noPopulate: false },
        header: any
    ) => {
        try {
            const queryParams = new URLSearchParams({
                ...query,
                noPopulate: query.noPopulate?.toString() || "false",
            }).toString();
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-category/organization/${id}?${queryParams}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAuditRecordByIdWithTaskTemplates: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/get-audit-record-by-id-with-task-templates/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAllAuditRecord: async (
        body: {
            filters: Record<string, any>;
            pagination: { page: number; limit: number };
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/get-all-audit-records`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAllAuditRecordByOrganizationID: async (
        queryParams: { organizationId: string; status?: string },
        header: any
    ) => {
        const queryString = new URLSearchParams(queryParams).toString();
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/get-all-audit-records-by-organization?${queryString}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    /**
     * Aggregated audit summary for many orgs in a single call.
     * Pass `organizationIds` (array of org _ids) to scope; omit to get all reachable orgs.
     */
    getAuditSummaryByOrganizations: async (
        organizationIds: string[] | undefined,
        header: any
    ): Promise<{
        organizationId: string;
        totalAudits: number;
        completedAudits: number;
        meanRating: number | null;
        meanScore: number | null;
        lastAuditAt: string | null;
    }[]> => {
        try {
            const qs = organizationIds && organizationIds.length
                ? `?organizationIds=${organizationIds.join(",")}`
                : "";
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-summary-by-organizations${qs}`,
                null,
                header
            );
            return data.data?.data || [];
        } catch (error: any) {
            return [];
        }
    },
    getAuditFormById: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-form/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAuditRecordById: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-record/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAuditsForAuditor: async (organizationId: string, header: any) => {
        try {
            const queryParams = new URLSearchParams({
                organizationId,
            }).toString();
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audits-for-auditor?${queryParams}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAuditsForAuditee: async (organizationId: string, header: any) => {
        try {
            const queryParams = new URLSearchParams({
                organizationId,
            }).toString();
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audits-for-auditee?${queryParams}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAllAuditByAdminOrEvaluator: async (header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/get-all-audit-by-admin-or-evaluator`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAllAuditRecordByUserId: async (id: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-record-for-user/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    updateAuditForm: async (
        id: string,
        body: Partial<AuditFormCreateBody> & Record<string, unknown>,
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/audit/audit-form/${id}`,
                body,
                header
            );
            minimalToast({
                title: "Audit Form Updated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    updateAuditCategory: async (id: any, body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/audit/audit-category/${id}`,
                body,
                header
            );
            minimalToast({
                title: "Audit Category Updated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    updateAuditRecord: async (id: any, body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "PUT",
                `${configEnv.baseUrl}/audit/audit-record/${id}`,
                body,
                header
            );
            minimalToast({
                title: "Audit Record Updated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    deleteAuditForm: async (id: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "DELETE",
                `${configEnv.baseUrl}/audit/${id}`,
                null,
                header
            );
            minimalToast({
                title: "Audit Form Deleted",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    deleteAuditRecord: async (id: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "DELETE",
                `${configEnv.baseUrl}/audit/delete-audit-record/${id}`,
                null,
                header
            );
            minimalToast({
                title: "Audit Record Deleted",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    deleteAuditCategory: async (
        id: any,
        header: any,
        organizationId?: string
    ) => {
        try {
            // Add organizationId as query parameter if provided
            const url = organizationId
                ? `${configEnv.baseUrl}/audit/audit-category/${id}?organizationId=${organizationId}`
                : `${configEnv.baseUrl}/audit/audit-category/${id}`;

            const data = await AxiosRequest("DELETE", url, null, header);
            minimalToast({
                title: "Audit Category Deleted",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
        }
    },
    exportAuditDataToExcel: async (id: any, body: any, header: any) => {
        try {
            const response = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/export-audit-data-to-excel/${id}`,
                body,
                header
            );
            if (
                response.data &&
                response.data.data &&
                response.data.data.data
            ) {
                const blob = new Blob(
                    [new Uint8Array(response.data.data.data)],
                    {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }
                );
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "audit_data.xlsx";
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                console.error("Invalid response structure:", response);
                minimalToast({
                    title: "Failed to export audit data",
                    message: "Invalid server response",
                    type: NotificationSeverity.ERROR,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.MEDIUM,
                });
                throw new Error(
                    "Failed to export audit data: Invalid server response"
                );
            }
        } catch (error: any) {
            throw error;
        }
    },
    getOverallReport: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/audit-task-report`,
                body,
                header
            );
            minimalToast({
                title: "Overall Report Generated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to get overall report",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
        }
    },
    generateShortLink: async (
        id: any,
        header: any,
        body?: { isPublic?: boolean; regenerate?: boolean }
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/generate-short-link/${id}`,
                body ?? {},
                header
            );
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to generate short link",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
        }
    },
    getAuditForms: async (header: any, id: string) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-form/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    generateAISummary: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/generate-ai-summary`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    getAISummary: async (id: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/get-ai-summary/${id}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
        }
    },
    /** Phase 5: auditee post-session reflection + auditee_post context answers */
    submitPostSessionReflection: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/post-session-reflection`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    /** Phase 5: auditee or admin sign-off */
    signOffAudit: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/sign-off`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    /** Phase 5: principal marks report reviewed + optional comment */
    markPrincipalReview: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/principal-review`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    /** Phase 5: sign-off state + action-plan tasks (scoped by organizationId when provided) */
    getSignOffStatus: async (
        auditRecordId: string,
        header: any,
        organizationId?: string
    ) => {
        try {
            const qs =
                organizationId != null && String(organizationId).trim() !== ""
                    ? `?organizationId=${encodeURIComponent(String(organizationId))}`
                    : "";
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/sign-off-status/${auditRecordId}${qs}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    /**
     * Per-parameter historical scores + org cohort means.
     * Response includes cohortPercentile (0–100, fraction of cohort below this audit’s overall score)
     * and cohortSize.
     */
    getParameterHistory: async (auditRecordId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/parameter-history/${auditRecordId}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    submitMutualFeedback: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/mutual-feedback`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    getMutualFeedback: async (auditRecordId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/mutual-feedback/${auditRecordId}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            throw error;
        }
    },
    generateKPIsWithAI: async (
        body: any,
        header: any,
        options?: { silentSuccess?: boolean }
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/generate-kpis-ai`,
                body,
                header
            );
            if (!options?.silentSuccess) {
                minimalToast({
                    title: "KPIs Generated",
                    message: data.data.message,
                    type: NotificationSeverity.SUCCESS,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.MEDIUM,
                });
            }
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to generate KPIs",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            throw error;
        }
    },
    enhanceParameterWithAI: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/enhance-parameter-ai`,
                body,
                header
            );
            minimalToast({
                title: "Parameter Enhanced",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to enhance parameter",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
        }
    },
    generateTaskTemplatesAI: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/generate-task-templates-ai`,
                body,
                header
            );
            minimalToast({
                title: "Task Templates Generated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to generate task templates",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
        }
    },
    previewKPIsWithAI: async (body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/preview-kpis-ai`,
                body,
                header
            );
            minimalToast({
                title: "KPI Preview Generated",
                message: data.data.message,
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            minimalToast({
                title: "Failed to preview KPIs",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
        }
    },
    /**
     * Generate up to 3 parameters (with up to 3 criteria each) for an existing audit category using AI
     * @param categoryId - The audit category ID
     * @param body - { numberOfParameters, criteriaPerParameter, context, save }
     * @param header - Auth header
     */
    generateParametersForCategoryAI: async (
        categoryId: string,
        body: {
            numberOfParameters?: number;
            criteriaPerParameter?: number;
            context?: string;
            save?: boolean;
            categoryDescription?: string;
            organizationId: string;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/categories/${categoryId}/generate-parameters-ai`,
                body,
                header
            );
            if (body.save) {
                minimalToast({
                    title: "Parameters Added",
                    message:
                        data.data.message ||
                        "Parameters generated and saved successfully",
                    type: NotificationSeverity.SUCCESS,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.MEDIUM,
                });
            }
            return data.data;
        } catch (error: any) {
            if (body.save) {
                minimalToast({
                    title: "Error",
                    message:
                        error?.response?.data?.message ||
                        "Failed to add parameters",
                    type: NotificationSeverity.ERROR,
                    category: NotificationCategory.AUDIT,
                    priority: NotificationPriority.MEDIUM,
                });
            }
            throw error;
        }
    },

    /**
     * Get previously generated dashboard reports for an organization
     * @param organizationId - Organization ID
     * @param params - Query parameters for filtering (limit, page, dateRange, reportType)
     * @param header - Auth header
     */
    getPreviousReports: async (
        organizationId: string,
        params: any = {},
        header: any
    ) => {
        try {
            const queryString = new URLSearchParams({
                organizationId,
                limit: params.limit || "10",
                page: params.page || "1",
                ...(params.reportType && { reportType: params.reportType }),
                ...(params.startDate && { startDate: params.startDate }),
                ...(params.endDate && { endDate: params.endDate }),
            }).toString();

            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/dashboard-reports?${queryString}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Error fetching previous reports:", error);
            minimalToast({
                title: "Failed to load previous reports",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            throw error;
        }
    },

    /**
     * Get a specific dashboard report by ID
     * @param reportId - Report ID
     * @param header - Auth header
     */
    getDashboardReportById: async (reportId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/dashboard-reports/${reportId}`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Error fetching dashboard report:", error);
            minimalToast({
                title: "Failed to load dashboard report",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            throw error;
        }
    },

    /**
     * Delete a dashboard report by ID
     * @param reportId - Report ID
     * @param header - Auth header
     */
    deleteDashboardReport: async (reportId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "DELETE",
                `${configEnv.baseUrl}/audit/dashboard-reports/${reportId}`,
                null,
                header
            );
            minimalToast({
                title: "Report Deleted",
                message: "Dashboard report deleted successfully",
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            console.error("Error deleting dashboard report:", error);
            minimalToast({
                title: "Failed to delete report",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            throw error;
        }
    },
    updateCriterion: async (
        criterionId: string,
        expectations: any,
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "PATCH",
                `${configEnv.baseUrl}/audit/criterion/${criterionId}`,
                { expectations },
                header
            );
            minimalToast({
                title: "Criterion Updated",
                message: "Expectations updated successfully",
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            console.error("Error updating criterion:", error);
            minimalToast({
                title: "Failed to update criterion",
                message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unknown error",
                type: NotificationSeverity.ERROR,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            throw error;
        }
    },
    
    /**
     * Publish audit with optional AI report generation using existing APIs in sequence
     * @param auditId - The audit record ID to publish  
     * @param generateAIReport - Whether to generate AI report
     * @param header - Auth header
     */
    publishAuditWithReportType: async (
        auditId: string,
        generateAIReport: boolean,
        header: any,
        organizationId?: string
    ) => {
        try {
            // Step 1: Publish the audit using existing togglePublishAudit API
            const publishResponse = await auditApi.togglePublishAudit(
                auditId,
                { isPublished: true },
                header
            );

            let aiSummary = null;

            // Step 2: Generate AI summary if requested using existing generateAISummary API
            if (generateAIReport) {
                try {
                    const aiResponse = await auditApi.generateAISummary(
                        { 
                            auditId,
                            organizationId 
                        },
                        header
                    );
                    aiSummary = aiResponse?.data;
                } catch (aiError) {
                    console.warn("AI summary generation failed, but audit was published:", aiError);
                    // Don't fail the entire operation if AI generation fails
                    throw aiError; // Re-throw to show the actual error to user
                }
            }

            return {
                auditRecord: publishResponse?.data,
                aiSummary,
                reportType: generateAIReport ? 'ai-enhanced' : 'standard'
            };
        } catch (error: any) {
            console.error("Error publishing audit with report type:", error);
            throw error;
        }
    },

    exportToAnecdotalRecord: async (
        auditRecordId: string,
        body: {
            targetAnecdotalRecordId?: string;
            createNew?: boolean;
            newRecordName?: string;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/export-to-anecdotal/${auditRecordId}`,
                body,
                header
            );
            minimalToast({
                title: "Exported to Anecdotal Record",
                message: "Audit successfully exported",
                type: NotificationSeverity.SUCCESS,
                category: NotificationCategory.AUDIT,
                priority: NotificationPriority.MEDIUM,
            });
            return data.data;
        } catch (error: any) {
            console.error("Export to anecdotal record error", error);
            throw error;
        }
    },
    getAuditByShortLink: async (
        shortLink: string,
        header?: Record<string, string> | null
    ) => {
        try {
            const mergedHeaders = {
                "Content-Type": "application/json",
                ...(header || {}),
            };
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/short-link/${shortLink}`,
                null,
                mergedHeaders
            );
            return data.data;
        } catch (error: any) {
            console.error("Get audit by short link error", error);
            throw error;
        }
    },
    getAiDirectiveTemplates: async () => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/ai-directive-templates`,
                null,
                {}
            );
            return data.data;
        } catch (error: any) {
            console.error("Get AI directive templates error", error);
            throw error;
        }
    },
    submitAuditorPreSession: async (auditId: string, body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/audit-record/${auditId}/auditor-pre-session`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Submit auditor pre-session error", error);
            throw error;
        }
    },
    submitAuditeeCrossCheck: async (auditId: string, body: any, header: any) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/audit-record/${auditId}/auditee-cross-check`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Submit auditee cross-check error", error);
            throw error;
        }
    },
    mergeAuditContextAnswers: async (
        auditId: string,
        body: {
            contextAnswers: Array<{
                questionId: string;
                answeredAs: string;
                answer: unknown;
            }>;
        },
        header: any
    ) => {
        try {
            const data = await AxiosRequest(
                "POST",
                `${configEnv.baseUrl}/audit/audit-record/${auditId}/merge-context-answers`,
                body,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Merge context answers error", error);
            throw error;
        }
    },
    getAuditReport: async (auditId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-record/${auditId}/report`,
                null,
                header
            );
            return data.data;
        } catch (error: any) {
            console.error("Get audit report error", error);
            throw error;
        }
    },
    getMutualFeedbackProfile: async (userId: string, role: 'auditor' | 'auditee', header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/users/${userId}/mutual-feedback-profile?role=${role}`,
                null,
                header
            );
            const body = data.data as { success?: boolean; data?: unknown; message?: string } | null;
            if (
                body &&
                typeof body === "object" &&
                "data" in body &&
                body.data != null &&
                typeof body.data === "object"
            ) {
                return body.data;
            }
            return body;
        } catch (error: any) {
            console.error("Get mutual feedback profile error", error);
            throw error;
        }
    },
    getMutualFeedbackBreakdown: async (auditId: string, header: any) => {
        try {
            const data = await AxiosRequest(
                "GET",
                `${configEnv.baseUrl}/audit/audit-record/${auditId}/mutual-feedback-breakdown`,
                null,
                header
            );
            const body = data.data as { success?: boolean; data?: unknown; message?: string } | null;
            if (
                body &&
                typeof body === "object" &&
                "data" in body &&
                body.data != null &&
                typeof body.data === "object"
            ) {
                return body.data;
            }
            return body;
        } catch (error: any) {
            console.error("Get mutual feedback breakdown error", error);
            throw error;
        }
    },
};
